import { Injectable } from '@angular/core';
import { CanActivateChild, ActivatedRouteSnapshot } from '@angular/router';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { FeatureService } from './shared/services/feature.service';
import { RedirectService } from '../shared/services/redirect.service';
import { ENV } from '../environment';
import { AccountService } from '../shared/services/account.service';
import { LaunchDarklyService } from '../shared/services/launchdarkly.service';

@Injectable({
  providedIn: 'root'
})
export class InstallNavigationGuard implements CanActivateChild {

  constructor(private featureService: FeatureService,
              private redirectService: RedirectService,
              private launchDarklyService: LaunchDarklyService,
              private accountService: AccountService) { }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean> {
    if (this.shouldRedirectToEmbeddedApp(childRoute)) {
      return this.embeddedAppUrl.pipe(
        take(1),
        map(url => {
          this.redirectService.redirectTo(url);
          return false;
        }),
        catchError(() => {
          return of(false);
        })
      );
    } else {
      return combineLatest([
        this.featureService.isFeatureEnabled('OnboardingV2'),
        this.featureService.isFeatureEnabled('Postman'),
        from(this.launchDarklyService.serviceReady)
      ]).pipe(
        take(1),
        map(([isNewOnboardingFeatureEnabled, isPostmanFeatureEnabled]) => {
          if (isNewOnboardingFeatureEnabled && isPostmanFeatureEnabled) {
            this.redirectService.redirectTo(ENV.ReviewsAppDomain + '#/onboarding');
            return false;
          }
          return true;
        }),
        catchError(() => {
          return of(false);
        })
      );
    }
  }

  private shouldRedirectToEmbeddedApp(childRoute: ActivatedRouteSnapshot): boolean {
    const embedded = childRoute.queryParams['embedded'];
    return embedded === '1' || embedded === 'true';
  }

  private get embeddedAppUrl(): Observable<string> {
    return this.getStoreDomain().pipe(
      take(1),
      map(domain => `https://${domain}/admin/apps/${ENV.ReviewsEmbeddedAppHandle}/onboarding`)
    );
  }

  private getStoreDomain(): Observable<string> {
    return this.accountService.getAppData().pipe(
      take(1),
      map(response => {
        let domain = response?.response?.app?.account_platform?.shop_domain;
        if (domain) {
          return domain;
        }
        throw new Error('Missing domain for store in account response');
      })
    );
  }
}
